import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { AxiosInstance } from 'axios'
import {
  ApiComponents,
  ArrayLocationAPIResponse,
  ConstructionStatusAPIResponse,
  ContractTypeAPIResponse,
  DocumentRequirement,
  DocumentRequirementsChecklist,
  EnergyTechnologyAPIResponse,
  SiteRFPDetails,
  SiteRFPDetailsResponseBody,
  SiteRFPMilestoneTypeAPIResponse,
  SiteRFPPhaseAPIResponse,
  TenantConsentStatusAPIResponse,
} from '@black-bear-energy/black-bear-energy-common'
import { requirementIsChecklist } from 'components/helpers/Sites/requirementIsChecklist'
import { parseNullableEnum } from './helpers/parseNullableEnum'
import { useApiWithIDTokenGranted } from '../../state/modules/common'
import { parseEnum } from './helpers/parseEnum'

type DocumentRequirementsAPI =
  | ApiComponents['schemas']['DocumentRequirement']
  | {
      name: string
      children: (
        | ApiComponents['schemas']['DocumentRequirement']
        | DocumentRequirementsAPI
      )[]
      defaultHidden: boolean
      isDocumentCollection: boolean
    }

/**
 * Fetch the details for a site RFP.
 * @param siteRFPId - The ID of the site RFP.
 * @returns A UseQueryResult object containing the site RFP details.
 */
export const useSiteRFPDetails = (
  siteRFPId: number
): UseQueryResult<SiteRFPDetails, Error> => {
  const api = useApiWithIDTokenGranted()

  return useQuery({
    queryKey: ['siteRFPDetails', api, siteRFPId],
    queryFn: () => fetchSiteRFPDetails(api as AxiosInstance, siteRFPId),
    select: transformSiteRFPDetails,
    enabled: !!api,
  })
}

async function fetchSiteRFPDetails(
  api: AxiosInstance,
  siteRFPId: number
): Promise<SiteRFPDetailsResponseBody> {
  const response = await api.get<SiteRFPDetailsResponseBody>(
    `/site-rfps/${siteRFPId}/details`
  )
  return response.data
}

function transformDocRequirement(
  req: DocumentRequirementsAPI
): DocumentRequirementsChecklist | DocumentRequirement {
  if (requirementIsChecklist(req)) {
    return {
      name: req.name,
      children: req.children.map((child) => transformDocRequirement(child)),
      defaultHidden: req.defaultHidden,
      isDocumentCollection: req.isDocumentCollection,
    }
  }
  return {
    id: req.id,
    name: req.name,
    order: req.order,
    uploaded: req.uploaded,
    review: req.review
      ? {
          verified: req.review.verified,
          outdated: req.review.outdated,
        }
      : null,
    defaultHidden: req.defaultHidden,
  }
}

function transformSiteRFPDetails(
  responseElement: SiteRFPDetailsResponseBody
): SiteRFPDetails {
  const milestones = responseElement.milestones.map((milestone) => ({
    type: parseEnum(
      milestone.type,
      SiteRFPMilestoneTypeAPIResponse,
      'milestone type'
    ),
    estimatedDate: milestone.estimatedDate
      ? new Date(milestone.estimatedDate)
      : null,
    submittedDate: milestone.submittedDate
      ? new Date(milestone.submittedDate)
      : null,
    actualDate: milestone.actualDate ? new Date(milestone.actualDate) : null,
  }))

  const documentRequirements = responseElement.documentRequirements.map(
    (reqChecklist) =>
      ({
        name: reqChecklist.name,
        children: reqChecklist.children.map(transformDocRequirement),
        defaultHidden: reqChecklist.defaultHidden,
        isDocumentCollection: reqChecklist.isDocumentCollection,
      } satisfies DocumentRequirementsChecklist)
  )

  return {
    ...responseElement,
    phase: parseEnum(responseElement.phase, SiteRFPPhaseAPIResponse, 'phase'),
    arrayLocation: parseNullableEnum(
      responseElement.arrayLocation,
      ArrayLocationAPIResponse,
      'array location'
    ),
    energyTechnology: parseNullableEnum(
      responseElement.energyTechnology,
      EnergyTechnologyAPIResponse,
      'energy technology'
    ),
    contractType: parseNullableEnum(
      responseElement.contractType,
      ContractTypeAPIResponse,
      'contract type'
    ),
    constructionStatus: parseNullableEnum(
      responseElement.constructionStatus,
      ConstructionStatusAPIResponse,
      'construction status'
    ),
    tenantConsentStatus: parseNullableEnum(
      responseElement.tenantConsentStatus,
      TenantConsentStatusAPIResponse,
      'tenant consent status'
    ),
    rentCommencementDate: responseElement.rentCommencementDate
      ? new Date(responseElement.rentCommencementDate)
      : null,
    documentRequirements,
    milestones,
  }
}
